'use client';

import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { ShoppingCart, CheckCircle2, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from 'sonner';
import {
  type Document,
  DocumentType as DocumentTypeEnum,
  ORDER_DOCUMENT_MUTATION,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { ConfirmDialog } from '@paeffgen/react/crm-documents/feature-material-requests';

interface OrderOfferButtonProps {
  document: Document;
  /** true → Button füllt die volle Breite (mobiler Footer). */
  fullWidth?: boolean;
  className?: string;
}

/**
 * "Angebot bestellen" — verbindliche Bestellung eines Angebots durch den Kunden.
 *
 * Wird nur für Belegtyp ANGEBOT gerendert (sonst `null`). Ablauf: Klick öffnet
 * eine Bestätigungsabfrage (versehentliches Bestellen vermeiden) → bei Bestätigung
 * `orderDocument`-Mutation. Ein `optimisticResponse` schaltet den Button sofort
 * auf den bestellten Zustand (Sofort-Feedback); scheitert der Server, rollt Apollo
 * zurück und eine Fehler-Toast erscheint. Der bestellte Zustand (`isOrdered`) ist
 * Beleg-/Debitor-weit persistent — der Button bleibt danach dauerhaft gesperrt.
 */
export function OrderOfferButton({ document, fullWidth, className }: OrderOfferButtonProps) {
  const { adminQueryVars } = useAdmin();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orderDocument, { loading }] = useMutation(ORDER_DOCUMENT_MUTATION);

  if (document.type !== DocumentTypeEnum.ANGEBOT) return null;

  const ordered = document.isOrdered === true;

  const baseBtn = clsx(
    'flex items-center justify-center gap-2 rounded-lg font-bold text-sm transition-colors',
    fullWidth ? 'w-full h-11' : 'px-4 py-2',
  );

  if (ordered) {
    return (
      <div className={clsx(fullWidth && 'w-full', className)}>
        <div
          className={clsx(
            baseBtn,
            'border border-green-200 bg-green-50 text-green-700 cursor-default',
          )}
          aria-disabled="true"
        >
          <CheckCircle2 size={18} />
          Angebot bestellt
        </div>
        {document.orderedAt && (
          <p className="mt-1 text-center text-xs text-gray-500">
            Bestellt am {new Date(document.orderedAt).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>
    );
  }

  const handleConfirm = () => {
    void orderDocument({
      variables: { documentId: document.id, ...adminQueryVars },
      optimisticResponse: {
        orderDocument: {
          __typename: 'Document',
          id: document.id,
          isOrdered: true,
          orderedAt: new Date().toISOString(),
        },
      },
    })
      .then(() =>
        toast.success('Angebot bestellt — Ihr Ansprechpartner wurde per E-Mail informiert.'),
      )
      .catch(() =>
        toast.error('Die Bestellung konnte nicht abgeschickt werden. Bitte erneut versuchen.'),
      );
  };

  return (
    <div className={clsx(fullWidth && 'w-full', className)}>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={loading}
        className={clsx(
          baseBtn,
          'bg-primary text-white shadow-sm hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        {loading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <ShoppingCart size={18} />
        )}
        Angebot bestellen
      </button>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title="Angebot bestellen"
        message={
          <>
            Möchten Sie das Angebot <strong>{document.documentNumber}</strong> verbindlich
            bestellen? Ihr Ansprechpartner wird per E-Mail informiert.
          </>
        }
        confirmLabel="Jetzt bestellen"
        cancelLabel="Abbrechen"
      />
    </div>
  );
}
